import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";

const FIELDS = [
  ["owner_name", "Customer / owner name"],
  ["contact", "Contact number"],
  ["email", "Email"],
  ["mailing_address", "Mailing address"],
  ["vehicle_model", "Vehicle model"],
  ["registration_no", "Registration no."],
  ["chassis_no", "Chassis no."],
  ["serial_number", "PPF serial no."],
  ["install_date", "Install date"],
  ["notes", "Notes"],
];

const STATUS_BADGE = {
  submitted: { label: "Submitted — awaiting CRM registration", cls: "bg-amber-100 text-amber-800" },
  registered: { label: "Warranty registered", cls: "bg-emerald-100 text-emerald-700" },
};
const PLAN_BADGE = {
  pending_payment_confirmation: { label: "Awaiting Accountant confirmation", cls: "bg-amber-100 text-amber-800" },
  payment_confirmed: { label: "Payment confirmed — awaiting CRM registration", cls: "bg-blue-100 text-blue-800" },
  active: { label: "Protection Plan active", cls: "bg-emerald-100 text-emerald-700" },
  rejected: { label: "Payment rejected — resubmit proof", cls: "bg-rose-100 text-rose-700" },
  expired: { label: "Expired", cls: "bg-slate-100 text-slate-600" },
};

export function WarrantyCredentials({ job, onChanged }) {
  const jobId = job.id;
  const fileRef = useRef();
  const [form, setForm] = useState(null);
  const [status, setStatus] = useState("");
  const [plan, setPlan] = useState(null);
  const [optIn, setOptIn] = useState(false);
  const [proof, setProof] = useState({ str_number: "", proof_path: "", proof_filename: "", payment_amount: "", area_sqft_covered: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [wf, pp] = await Promise.all([
      api.get(`/jobs/${jobId}/warranty-form`).then((r) => r.data).catch(() => null),
      api.get(`/jobs/${jobId}/protection-plan`).then((r) => r.data).catch(() => null),
    ]);
    const f = wf?.warranty_form || {};
    setForm({
      owner_name: f.owner_name || job.customer_name || "",
      contact: f.contact || job.customer_phone || "",
      email: f.email || "",
      mailing_address: f.mailing_address || "",
      vehicle_model: f.vehicle_model || `${job.vehicle?.make || ""} ${job.vehicle?.model || ""}`.trim(),
      registration_no: f.registration_no || job.vehicle?.plate || "",
      chassis_no: f.chassis_no || job.vehicle?.vin || "",
      serial_number: f.serial_number || "",
      install_date: f.install_date || (job.completed_at || "").slice(0, 10),
      warranty_type: f.warranty_type || "PPF",
      warranty_years: f.warranty_years || 5,
      notes: f.notes || "",
    });
    setStatus(wf?.warranty_form_status || "");
    setPlan(pp);
    if (pp) setOptIn(true);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [jobId]);

  const saveForm = async () => {
    setBusy(true);
    try {
      await api.post(`/jobs/${jobId}/warranty-form`, { ...form, warranty_years: Number(form.warranty_years || 5) });
      toast.success("Warranty details submitted to CRM");
      await load();
      onChanged?.();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const uploadProof = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setProof((p) => ({ ...p, proof_path: data.path, proof_filename: data.filename }));
      toast.success("Proof uploaded");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const submitProtection = async () => {
    setBusy(true);
    try {
      const payload = {
        job_id: jobId, customer_name: form.owner_name, contact: form.contact,
        email: form.email, vehicle_model: form.vehicle_model, registration_no: form.registration_no,
        chassis_no: form.chassis_no, serial_number: form.serial_number,
        area_sqft_covered: Number(proof.area_sqft_covered || 0),
        str_number: proof.str_number, proof_path: proof.proof_path, proof_filename: proof.proof_filename,
        payment_amount: proof.payment_amount ? Number(proof.payment_amount) : null,
      };
      if (plan && plan.status === "rejected") {
        await api.post(`/protection-plans/${plan.id}/resubmit`, {
          str_number: proof.str_number, proof_path: proof.proof_path,
          proof_filename: proof.proof_filename,
          payment_amount: proof.payment_amount ? Number(proof.payment_amount) : null,
        });
      } else {
        await api.post("/protection-plans", payload);
      }
      toast.success("Protection Plan sent to Accountant for confirmation");
      await load();
      onChanged?.();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!form) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const badge = STATUS_BADGE[status];

  return (
    <Card className="mt-4 border-brand-line shadow-none" data-testid="warranty-credentials-card">
      <CardHeader className="flex-row flex-wrap items-center justify-between gap-3 space-y-0">
        <CardTitle className="text-base">Warranty registration details</CardTitle>
        {badge && <Badge className={`${badge.cls} border-0`} data-testid="warranty-form-status">{badge.label}</Badge>}
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2">
          {FIELDS.map(([k, label]) => (
            <div key={k} className={k === "mailing_address" || k === "notes" ? "sm:col-span-2" : ""}>
              <Label className="text-xs">{label}</Label>
              <Input
                type={k === "install_date" ? "date" : "text"}
                data-testid={`wc-${k}`}
                className="mt-1 min-h-[42px]"
                value={form[k]}
                onChange={(e) => set(k, e.target.value)}
              />
            </div>
          ))}
        </div>
        <Button onClick={saveForm} disabled={busy} data-testid="wc-save-form" className="min-h-[44px]">
          {status === "registered" ? "Update details" : "Submit warranty details"}
        </Button>

        {/* Protection Plan opt-in */}
        <div className="rounded-lg border border-brand-line p-3">
          <label className="flex items-center gap-2 text-sm font-medium">
            <input type="checkbox" data-testid="wc-protection-optin" checked={optIn}
              disabled={plan && plan.status === "active"}
              onChange={(e) => setOptIn(e.target.checked)} />
            Customer has opted for Protection Plan
          </label>

          {plan && (
            <div className="mt-2">
              <Badge className={`${(PLAN_BADGE[plan.status] || {}).cls || "bg-slate-100 text-slate-600"} border-0`} data-testid="wc-plan-status">
                {(PLAN_BADGE[plan.status] || {}).label || plan.status}
              </Badge>
              {plan.status === "rejected" && plan.rejection_reason && (
                <p className="mt-1 text-xs text-rose-600" data-testid="wc-reject-reason">Reason: {plan.rejection_reason}</p>
              )}
              {plan.certificate_number && <p className="mt-1 text-xs text-slate-600">Certificate: {plan.certificate_number}</p>}
            </div>
          )}

          {optIn && (!plan || plan.status === "rejected") && (
            <div className="mt-3 space-y-3">
              <p className="text-xs text-slate-500">Attach the studio→HQ payment proof (image/PDF) or enter the STR / payment reference.</p>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <Label className="text-xs">Area covered (sq. ft.)</Label>
                  <Input inputMode="decimal" data-testid="wc-area" className="mt-1 min-h-[42px]"
                    value={proof.area_sqft_covered} onChange={(e) => setProof((p) => ({ ...p, area_sqft_covered: e.target.value }))} />
                </div>
                <div>
                  <Label className="text-xs">STR / payment reference</Label>
                  <Input data-testid="wc-str" className="mt-1 min-h-[42px]"
                    value={proof.str_number} onChange={(e) => setProof((p) => ({ ...p, str_number: e.target.value }))} />
                </div>
                <div>
                  <Label className="text-xs">Plan amount (₹)</Label>
                  <Input inputMode="decimal" data-testid="wc-amount" className="mt-1 min-h-[42px]"
                    value={proof.payment_amount} onChange={(e) => setProof((p) => ({ ...p, payment_amount: e.target.value }))} />
                </div>
                <div>
                  <Label className="text-xs">Proof of payment (image/PDF)</Label>
                  <input ref={fileRef} type="file" accept="image/*,application/pdf" data-testid="wc-proof-file"
                    className="mt-1 block w-full text-xs" onChange={uploadProof} />
                  {proof.proof_filename && <p className="mt-1 text-xs text-emerald-600">Attached: {proof.proof_filename}</p>}
                </div>
              </div>
              <Button onClick={submitProtection} disabled={busy} data-testid="wc-submit-protection" className="min-h-[44px]">
                {plan && plan.status === "rejected" ? "Resubmit proof" : "Submit for Accountant confirmation"}
              </Button>
            </div>
          )}
        </div>

        {/* Manual pipeline completion fallback */}
        {status === "registered" && job.status !== "Warranty/Protection Plan" && (
          <Button variant="outline" className="min-h-[44px] w-full border-brand-line" data-testid="wc-complete-stage"
            onClick={async () => {
              try { await api.post(`/jobs/${jobId}/complete-warranty-stage`); toast.success("Job moved to Warranty/Protection Plan"); onChanged?.(); }
              catch (e) { toast.error(apiError(e.response?.data?.detail)); }
            }}>
            Mark warranty & protection complete
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
